/**
 * PersonalDataTab Component
 * View/edit personal profile data and change password
 */

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { Pencil, X, Check, Mail, CheckCircle, AlertCircle, Loader2, Eye, EyeOff } from 'lucide-react';
import { CollapsibleGlassCard } from '@/components/common/GlassCard';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/stores/authStore';
import { authService } from '@/services/api/auth';
import { eventService } from '@/services/tracking/eventService';
import type { UpdateProfileRequest, SetPasswordRequest } from '@/types/profile';

const inputClassName =
  'h-12 bg-white border-gray-200 focus:border-[var(--color-primary-start)] focus:ring-[var(--color-primary-start)]';
const labelClassName = 'text-[var(--color-charcoal)] font-medium';

const profileSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  jobTitle: z.string().optional(),
  phoneNumber: z.string().optional(),
  organization: z.string().optional(),
  street: z.string().optional(),
  houseNumber: z.string().optional(),
  postalCode: z.string().optional(),
  city: z.string().optional(),
  country: z.string().optional(),
});

type ProfileFormData = z.infer<typeof profileSchema>;

const passwordSchema = z.object({
  currentPassword: z.string().min(1),
  newPassword: z.string().min(8),
  confirmPassword: z.string().min(1),
}).refine((data) => data.newPassword === data.confirmPassword, {
  path: ['confirmPassword'],
  message: 'Passwords must match',
});

type PasswordFormData = z.infer<typeof passwordSchema>;

export function PersonalDataTab() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const [isEditing, setIsEditing] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [trackingOptOut, setTrackingOptOut] = useState(user?.trackingOptOut ?? false);

  // Profile form
  const {
    register: registerProfile,
    handleSubmit: handleProfileSubmit,
    reset: resetProfile,
    formState: { errors: profileErrors },
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstName: user?.firstName ?? '',
      lastName: user?.lastName ?? '',
      jobTitle: user?.jobTitle ?? '',
      phoneNumber: user?.phoneNumber ?? '',
      organization: user?.organization ?? '',
      street: user?.street ?? '',
      houseNumber: user?.houseNumber ?? '',
      postalCode: user?.postalCode ?? '',
      city: user?.city ?? '',
      country: user?.country ?? '',
    },
  });

  // Password form
  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    reset: resetPassword,
    formState: { errors: passwordErrors },
  } = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
  });

  const updateProfileMutation = useMutation({
    mutationFn: (data: UpdateProfileRequest) => authService.updateProfile(data),
    onSuccess: (updatedUser) => {
      setUser(updatedUser);
      setIsEditing(false);
      setProfileSuccess(true);
      eventService.refreshOptOut();
      setTimeout(() => setProfileSuccess(false), 3000);
    },
  });

  const setPasswordMutation = useMutation({
    mutationFn: (data: SetPasswordRequest) => authService.setPassword(data),
    onSuccess: () => {
      resetPassword();
      setShowPassword(false);
      setPasswordSuccess(true);
      setTimeout(() => setPasswordSuccess(false), 3000);
    },
  });

  const verifyEmailMutation = useMutation({
    mutationFn: () => authService.sendEmailVerification(),
  });

  const onProfileSubmit = (data: ProfileFormData) => {
    updateProfileMutation.mutate(data);
  };

  const onPasswordSubmit = (data: PasswordFormData) => {
    setPasswordMutation.mutate(data);
  };

  const handleCancelEdit = () => {
    resetProfile({
      firstName: user?.firstName ?? '',
      lastName: user?.lastName ?? '',
      jobTitle: user?.jobTitle ?? '',
      phoneNumber: user?.phoneNumber ?? '',
      organization: user?.organization ?? '',
      street: user?.street ?? '',
      houseNumber: user?.houseNumber ?? '',
      postalCode: user?.postalCode ?? '',
      city: user?.city ?? '',
      country: user?.country ?? '',
    });
    setIsEditing(false);
  };

  return (
    <div className="space-y-6">
      {/* Personal Information */}
      <CollapsibleGlassCard
        title={t('profile.personalData.title')}
        headerActions={
          !isEditing ? (
            <Button variant="ghost" size="sm" onClick={() => setIsEditing(true)} className="gap-1.5">
              <Pencil className="w-4 h-4" />
              {t('profile.personalData.edit')}
            </Button>
          ) : (
            <Button variant="ghost" size="sm" onClick={handleCancelEdit} className="gap-1.5">
              <X className="w-4 h-4" />
              {t('profile.personalData.cancel')}
            </Button>
          )
        }
      >
          {profileSuccess && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700 text-sm">
              <CheckCircle className="w-4 h-4" />
              {t('profile.personalData.updateSuccess')}
            </div>
          )}

          <form onSubmit={handleProfileSubmit(onProfileSubmit)}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Email (read-only) */}
              <div className="sm:col-span-2">
                <Label className={labelClassName}>{t('profile.personalData.email')}</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    value={user?.email ?? ''}
                    disabled
                    className={`${inputClassName} bg-gray-50`}
                  />
                  {user?.emailVerified ? (
                    <span className="flex items-center gap-1 text-green-600 text-xs whitespace-nowrap">
                      <CheckCircle className="w-4 h-4" />
                      {t('profile.personalData.verified')}
                    </span>
                  ) : (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => verifyEmailMutation.mutate()}
                      disabled={verifyEmailMutation.isPending}
                      className="text-xs whitespace-nowrap gap-1"
                    >
                      {verifyEmailMutation.isPending ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : verifyEmailMutation.isSuccess ? (
                        <Check className="w-3 h-3 text-green-600" />
                      ) : (
                        <Mail className="w-3 h-3" />
                      )}
                      {verifyEmailMutation.isSuccess
                        ? t('profile.personalData.verificationSent')
                        : t('profile.personalData.resendVerification')}
                    </Button>
                  )}
                </div>
              </div>

              {/* First Name */}
              <div>
                <Label className={labelClassName}>{t('profile.personalData.firstName')}</Label>
                {isEditing ? (
                  <>
                    <Input {...registerProfile('firstName')} className={inputClassName} />
                    {profileErrors.firstName && (
                      <p className="text-sm text-red-500 mt-1">{profileErrors.firstName.message}</p>
                    )}
                  </>
                ) : (
                  <p className="mt-1 py-3 text-[var(--color-charcoal)]">{user?.firstName || '-'}</p>
                )}
              </div>

              {/* Last Name */}
              <div>
                <Label className={labelClassName}>{t('profile.personalData.lastName')}</Label>
                {isEditing ? (
                  <>
                    <Input {...registerProfile('lastName')} className={inputClassName} />
                    {profileErrors.lastName && (
                      <p className="text-sm text-red-500 mt-1">{profileErrors.lastName.message}</p>
                    )}
                  </>
                ) : (
                  <p className="mt-1 py-3 text-[var(--color-charcoal)]">{user?.lastName || '-'}</p>
                )}
              </div>

              {/* Organization */}
              <div>
                <Label className={labelClassName}>{t('profile.personalData.organization')}</Label>
                {isEditing ? (
                  <Input {...registerProfile('organization')} className={inputClassName} />
                ) : (
                  <p className="mt-1 py-3 text-[var(--color-charcoal)]">{user?.organization || '-'}</p>
                )}
              </div>

              {/* Job Title */}
              <div>
                <Label className={labelClassName}>{t('profile.personalData.jobTitle')}</Label>
                {isEditing ? (
                  <Input {...registerProfile('jobTitle')} className={inputClassName} />
                ) : (
                  <p className="mt-1 py-3 text-[var(--color-charcoal)]">{user?.jobTitle || '-'}</p>
                )}
              </div>

              {/* Phone */}
              <div>
                <Label className={labelClassName}>{t('profile.personalData.phone')}</Label>
                {isEditing ? (
                  <Input {...registerProfile('phoneNumber')} className={inputClassName} />
                ) : (
                  <p className="mt-1 py-3 text-[var(--color-charcoal)]">{user?.phoneNumber || '-'}</p>
                )}
              </div>

              {/* Address section header */}
              <div className="sm:col-span-2 pt-2">
                <p className="text-sm font-semibold text-gray-400 uppercase tracking-wide">{t('profile.personalData.addressSection')}</p>
              </div>

              {/* Street + House Number */}
              <div>
                <Label className={labelClassName}>{t('profile.personalData.street')}</Label>
                {isEditing ? (
                  <Input {...registerProfile('street')} className={inputClassName} />
                ) : (
                  <p className="mt-1 py-3 text-[var(--color-charcoal)]">{user?.street || '-'}</p>
                )}
              </div>

              <div>
                <Label className={labelClassName}>{t('profile.personalData.houseNumber')}</Label>
                {isEditing ? (
                  <Input {...registerProfile('houseNumber')} className={inputClassName} />
                ) : (
                  <p className="mt-1 py-3 text-[var(--color-charcoal)]">{user?.houseNumber || '-'}</p>
                )}
              </div>

              {/* Postal Code + City */}
              <div>
                <Label className={labelClassName}>{t('profile.personalData.postalCode')}</Label>
                {isEditing ? (
                  <Input {...registerProfile('postalCode')} className={inputClassName} />
                ) : (
                  <p className="mt-1 py-3 text-[var(--color-charcoal)]">{user?.postalCode || '-'}</p>
                )}
              </div>

              <div>
                <Label className={labelClassName}>{t('profile.personalData.city')}</Label>
                {isEditing ? (
                  <Input {...registerProfile('city')} className={inputClassName} />
                ) : (
                  <p className="mt-1 py-3 text-[var(--color-charcoal)]">{user?.city || '-'}</p>
                )}
              </div>

              {/* Country */}
              <div>
                <Label className={labelClassName}>{t('profile.personalData.country')}</Label>
                {isEditing ? (
                  <Input {...registerProfile('country')} className={inputClassName} />
                ) : (
                  <p className="mt-1 py-3 text-[var(--color-charcoal)]">{user?.country || '-'}</p>
                )}
              </div>
            </div>

            {isEditing && (
              <div className="flex justify-end mt-4">
                <Button
                  type="submit"
                  data-track="profile_save"
                  className="btn-primary"
                  disabled={updateProfileMutation.isPending}
                >
                  {updateProfileMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : null}
                  {t('profile.personalData.save')}
                </Button>
              </div>
            )}

            {updateProfileMutation.isError && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
                <AlertCircle className="w-4 h-4" />
                {t('profile.personalData.updateError')}
              </div>
            )}
          </form>
      </CollapsibleGlassCard>

      {/* Change Password */}
      <CollapsibleGlassCard title={t('profile.password.title')} defaultOpen={false}>
          {passwordSuccess && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700 text-sm">
              <CheckCircle className="w-4 h-4" />
              {t('profile.password.changeSuccess')}
            </div>
          )}

          <form onSubmit={handlePasswordSubmit(onPasswordSubmit)}>
            <div className="space-y-4">
              <div>
                <Label className={labelClassName}>{t('profile.password.current')}</Label>
                <div className="relative mt-1">
                  <Input
                    {...registerPassword('currentPassword')}
                    type={showPassword ? 'text' : 'password'}
                    className={`${inputClassName} pr-10`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {passwordErrors.currentPassword && (
                  <p className="text-sm text-red-500 mt-1">{t('validation.passwordRequired')}</p>
                )}
              </div>

              <div>
                <Label className={labelClassName}>{t('profile.password.new')}</Label>
                <div className="relative mt-1">
                  <Input
                    {...registerPassword('newPassword')}
                    type={showPassword ? 'text' : 'password'}
                    className={`${inputClassName} pr-10`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {passwordErrors.newPassword && (
                  <p className="text-sm text-red-500 mt-1">{t('validation.passwordMinLength')}</p>
                )}
              </div>

              <div>
                <Label className={labelClassName}>{t('profile.password.confirm')}</Label>
                <div className="relative mt-1">
                  <Input
                    {...registerPassword('confirmPassword')}
                    type={showPassword ? 'text' : 'password'}
                    className={`${inputClassName} pr-10`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {passwordErrors.confirmPassword && (
                  <p className="text-sm text-red-500 mt-1">{t('validation.passwordsMustMatch')}</p>
                )}
              </div>
            </div>

            <div className="flex justify-end mt-6">
              <Button
                type="submit"
                data-track="profile_password_change"
                className="btn-primary"
                disabled={setPasswordMutation.isPending}
              >
                {setPasswordMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                {t('profile.password.change')}
              </Button>
            </div>

            {setPasswordMutation.isError && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
                <AlertCircle className="w-4 h-4" />
                {t('profile.password.changeError')}
              </div>
            )}
          </form>
      </CollapsibleGlassCard>

      {/* Usage Analytics Opt-Out (Art. 21 DSGVO) */}
      <CollapsibleGlassCard title={t('profile.tracking.title', 'Nutzungsanalyse')} defaultOpen={false}>
        <div className="space-y-3">
          <p className="text-sm text-[var(--color-charcoal)]/70">
            {t('profile.tracking.description', 'Wir erheben serverseitige Nutzungsstatistiken zur Verbesserung unseres Dienstes (Art. 6 Abs. 1 lit. f DSGVO). Sie haben das Recht, der Nutzungsanalyse jederzeit zu widersprechen (Art. 21 DSGVO).')}
          </p>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={trackingOptOut}
              onChange={(e) => {
                const newValue = e.target.checked;
                setTrackingOptOut(newValue);
                updateProfileMutation.mutate({ trackingOptOut: newValue });
              }}
              className="w-4 h-4 rounded border-gray-300 text-[var(--color-primary-start)] focus:ring-[var(--color-primary-start)]"
            />
            <span className="text-sm text-[var(--color-charcoal)]">
              {t('profile.tracking.optOut', 'Nutzungsanalyse deaktivieren')}
            </span>
          </label>
        </div>
      </CollapsibleGlassCard>
    </div>
  );
}
